import React, { useEffect, useMemo, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';

import { useMailDrafts } from './MailDraftsContext';
import { getMembershipApplication } from '../../../../services/membershipApplications';

import {
  sendMembershipMail,
} from '../../../../services/membershipmailsService';

import {
  pickBestTo,
  renderMailDraft,
} from '../../../../services/mailTemplatesService';

import { setMembershipApplicationStatus } from '../../../../services/membershipApplications';

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export default function AcceptMailModal({
  open,
  app,
  onClose,
  onChanged,
  previousStatus, // Estado anterior para revertir si se cancela
}) {
  const { acceptanceDraft } = useMailDrafts();

  // Render del draft con tokens -> valores por defecto
  const rendered = useMemo(() => renderMailDraft(acceptanceDraft || { subject: '', body: '' }, app), [acceptanceDraft, app]);
  const defaultTo = useMemo(() => pickBestTo(app), [app]);

  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(defaultTo || '');
  const [ccLine, setCcLine] = useState('');
  const [subject, setSubject] = useState(rendered.subject);
  const [body, setBody] = useState(rendered.body);

  useEffect(() => {
    if (!open) return;
    const { subject: s, body: b } = renderMailDraft(acceptanceDraft || { subject: '', body: '' }, app);
    setSubject(s);
    setBody(b);
    setTo(pickBestTo(app) || '');
    setCcLine('');
  }, [open, acceptanceDraft, app]);

  if (!open || !app?.id) return null;

  const send = async () => {
    if (!to || !emailRx.test(to)) {
      Swal.fire('Correo inválido', 'Completa un destinatario válido.', 'warning');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      Swal.fire('Faltan datos', 'Completa asunto y cuerpo del correo.', 'warning');
      return;
    }

    // Obtener nombre de la empresa para el mensaje de confirmación
    const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';

    // Confirmar antes de enviar
    const confirmResult = await Swal.fire({
      title: '¿Estás seguro?',
      html: `¿Estás seguro de enviar el correo hacia <strong>${to.trim()}</strong> para la empresa <strong>${companyName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0488AF',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, enviar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmResult.isConfirmed) {
      return; // Usuario canceló
    }

    try {
      setSending(true);

      await sendMembershipMail(app.id, {
        template: 'ACCEPTED',
        to: to.trim(),
        cc: ccLine,                   // puede ser string (se normaliza en el service)
        subject: subject.trim(),
        body: body.trim(),
        copyToMe: true, // Siempre con copia por defecto
        attachDeclaration: false, // NO enviar declaración cuando se acepta (ya fue enviada antes)
        attachApplicationPdf: false,
      });

      // Recargar la aplicación para obtener el estado actualizado
      const updatedApp = await getMembershipApplication(app.id);
      const updated = updatedApp?.data || updatedApp || app;

      // Obtener nombre de la empresa
      const companyName = updated?.razonSocial || updated?.nombreFantasia || app?.razonSocial || app?.nombreFantasia || 'Sin nombre';

      Swal.fire('Enviado', `Correo de aceptación "${subject.trim()}" enviado correctamente a ${to.trim()} para la empresa <strong>${companyName}</strong>.`, 'success');
      onChanged?.(updated || app);
      onClose?.();
    } catch (e) {
      Swal.fire('Error', e?.message || 'No se pudo completar el envío.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm flex items-center justify-center p-2 sm:p-3">
      <div className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-[#0b3a66] via-[#0b6b93] to-[#0488AF] text-white flex items-center justify-between">
          <div className="font-semibold">Enviar correo de aceptación</div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm">
            <span className="text-base leading-none">ℹ️</span>
            <span>
              Este correo se envía cuando la postulación ya ha sido <span className="font-bold text-blue-900">aceptada en sesión de Directorio</span>. No incluye la declaración jurada (ya fue enviada anteriormente).
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">Para</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="correo@empresa.cl"
              />
            </label>
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">CC (opcional)</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={ccLine}
                onChange={(e) => setCcLine(e.target.value)}
                placeholder="cc1@dom.com, cc2@dom.com"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Asunto</div>
              <input
                className="w-full rounded-lg border px-3 h-10"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Asunto del correo"
              />
            </label>

            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Cuerpo</div>
              <textarea
                className="w-full rounded-lg border px-3 py-2 text-sm"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
          </div>

        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex justify-end gap-2 border-t bg-gray-50 flex-none">
          <button
            onClick={async () => {
              // Obtener nombre de la empresa
              const companyName = app?.razonSocial || app?.nombreFantasia || 'Sin nombre';
              
              const result = await Swal.fire({
                title: '¿Cancelar envío?',
                html: `Si cancelas, <strong>no se enviará el correo</strong> y <strong>no se efectuará el cambio de estado</strong>. La postulación de la empresa <strong>${companyName}</strong> permanecerá en su estado actual.`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#6b7280',
                cancelButtonColor: '#0488AF',
                confirmButtonText: 'Sí, cancelar',
                cancelButtonText: 'Continuar enviando',
              });
              
              if (result.isConfirmed) {
                // Si hay un estado anterior, revertir el estado antes de cerrar
                if (previousStatus && app?.id) {
                  try {
                    await setMembershipApplicationStatus(app.id, previousStatus);
                    const revertedApp = await getMembershipApplication(app.id);
                    const reverted = revertedApp?.data || revertedApp || app;
                    onChanged?.(reverted);
                  } catch (e) {
                    console.error('Error al revertir estado:', e);
                  }
                }
                onClose?.();
              }
            }}
            className="h-10 px-4 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={send}
            disabled={sending}
            className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2 text-sm"
          >
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Enviar correo de aceptación
          </button>
        </div>
      </div>
    </div>
  );
}
